import { createTheme, alpha } from "@mui/material/styles";
import { DESIGN_TOKENS } from "./designSystem/tokens";

export const getTheme = (mode = "light", brand = {}) => {
  const isDark = mode === "dark";
  const brandPrimary = brand.primaryColor || DESIGN_TOKENS.defaults.primaryColor;
  const brandSecondary = brand.secondaryColor || DESIGN_TOKENS.defaults.secondaryColor;
  const accent = brand.accentColor || DESIGN_TOKENS.defaults.accentColor;

  // Light mode uses primary color, Dark mode uses secondary color
  const primary = isDark ? brandSecondary : brandPrimary;
  const secondary = isDark ? brandPrimary : brandSecondary;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: primary,
        dark: secondary,
        contrastText: "#ffffff",
      },
      secondary: {
        main: accent,
        contrastText: "#ffffff",
      },
      success: { main: DESIGN_TOKENS.defaults.success },
      warning: { main: DESIGN_TOKENS.defaults.warning },
      error: { main: DESIGN_TOKENS.defaults.error },
      background: {
        default: isDark ? "#0B1220" : "#F4F7FB",
        paper: isDark ? "#111827" : "#FFFFFF",
      },
      text: {
        primary: isDark ? "#F8FAFC" : "#0F172A",
        secondary: isDark ? "#94A3B8" : "#64748B",
      },
      divider: isDark ? alpha("#94A3B8", 0.16) : "#E2E8F0",
      action: {
        hover: isDark ? alpha("#FFFFFF", 0.04) : alpha(primary, 0.04),
        selected: isDark ? alpha(primary, 0.16) : alpha(primary, 0.08),
      },
    },
    typography: {
      fontFamily: DESIGN_TOKENS.typography.fontFamily,
      h1: { fontWeight: 700, letterSpacing: "-0.02em" },
      h2: { fontWeight: 700, letterSpacing: "-0.02em" },
      h3: { fontWeight: 650, letterSpacing: "-0.01em" },
      h4: { fontWeight: 650 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      body2: { lineHeight: 1.55 },
    },
    shape: { borderRadius: DESIGN_TOKENS.radius.md },
    shadows: [
      "none",
      isDark ? DESIGN_TOKENS.shadowDark.sm : DESIGN_TOKENS.shadow.sm,
      isDark ? DESIGN_TOKENS.shadowDark.md : DESIGN_TOKENS.shadow.md,
      isDark ? DESIGN_TOKENS.shadowDark.lg : DESIGN_TOKENS.shadow.lg,
      ...Array(21).fill(
        isDark ? DESIGN_TOKENS.shadowDark.lg : DESIGN_TOKENS.shadow.lg
      ),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? "#0B1220" : "#F4F7FB",
            color: isDark ? "#F8FAFC" : "#0F172A",
            scrollbarWidth: "thin",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: DESIGN_TOKENS.radius.md,
            paddingInline: 16,
            paddingBlock: 8,
            fontWeight: 600,
          },
          containedPrimary: {
            backgroundColor: primary,
            "&:hover": { backgroundColor: secondary },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#0F172A" : "#FFFFFF",
            borderRadius: DESIGN_TOKENS.radius.md,
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: primary,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: isDark ? "#111827" : "#FFFFFF",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: DESIGN_TOKENS.radius.lg,
            border: `1px solid ${isDark ? alpha("#94A3B8", 0.12) : "#E2E8F0"}`,
            boxShadow: isDark
              ? DESIGN_TOKENS.shadowDark.sm
              : DESIGN_TOKENS.shadow.sm,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: DESIGN_TOKENS.radius.md,
            border: `1px solid ${isDark ? alpha("#94A3B8", 0.18) : "#E2E8F0"}`,
            boxShadow: isDark
              ? DESIGN_TOKENS.shadowDark.sm
              : DESIGN_TOKENS.shadow.sm,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: DESIGN_TOKENS.radius.sm,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: DESIGN_TOKENS.radius.md },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: DESIGN_TOKENS.radius.md },
        },
      },
    },
  });
};

export { DESIGN_TOKENS };
